import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  deleteKioskPermanently,
  listKiosks,
  provisionKiosk,
  revokeKiosk,
  rotateKiosk,
} from '@/lib/api';
import type { Facility, KioskListItem } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Check, Copy, Plus, RotateCcw, Tablet, Trash2 } from 'lucide-react';
import { Fragment, useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string | null): string {
  if (!iso) return 'never seen';
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KioskStatusPill({ revoked }: { revoked: boolean }) {
  if (revoked) {
    return (
      <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
        Revoked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
      Active
    </span>
  );
}

interface RowActionsProps {
  kiosk: KioskListItem;
  onRotate: () => void;
  onRevoke: () => void;
  onDelete: () => void;
  isRevoked: boolean;
}

function KioskRowActions({ onRotate, onRevoke, onDelete, isRevoked }: RowActionsProps) {
  if (isRevoked) {
    return (
      <button
        type="button"
        onClick={onDelete}
        className="flex items-center gap-1 text-xs text-destructive hover:underline press"
        title="Delete permanently"
      >
        <Trash2 className="size-3.5" strokeWidth={1.75} />
        Delete
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onRotate}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
        title="Rotate token"
      >
        <RotateCcw className="size-3.5" strokeWidth={1.75} />
        Rotate
      </button>
      <button
        type="button"
        onClick={onRevoke}
        className="flex items-center gap-1 text-xs text-destructive hover:underline press"
        title="Revoke kiosk"
      >
        <Trash2 className="size-3.5" strokeWidth={1.75} />
        Revoke
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provision modal
// ---------------------------------------------------------------------------

interface ProvisionModalProps {
  facilities: Facility[];
  onClose: () => void;
  onProvisioned: (kiosk: KioskListItem, token: string) => void;
}

function ProvisionModal({ facilities, onClose, onProvisioned }: ProvisionModalProps) {
  const [facilityId, setFacilityId] = useState(facilities[0]?.id ?? '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!facilityId || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { kiosk, token } = await provisionKiosk({ facilityId, name: name.trim() });
      onProvisioned(kiosk, token);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label="Provision new kiosk">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Provision new kiosk</h2>

        <div>
          <label
            htmlFor="provision-facility"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Facility
          </label>
          <select
            id="provision-facility"
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="provision-name"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Name
          </label>
          <input
            id="provision-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Front counter — Riverside"
            maxLength={80}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy && facilityId && name.trim()) void handleCreate();
            }}
          />
        </div>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!facilityId || !name.trim() || busy}
            onClick={() => void handleCreate()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Rotate confirmation modal
// ---------------------------------------------------------------------------

interface RotateConfirmProps {
  kiosk: KioskListItem;
  onCancel: () => void;
  onConfirmed: (token: string) => void;
}

function RotateConfirmModal({ kiosk, onCancel, onConfirmed }: RotateConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      const { token } = await rotateKiosk(kiosk.id);
      onConfirmed(token);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md" aria-label="Confirm token rotation">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Rotate token for "{kiosk.name}"?</h2>
        <p className="text-sm text-muted-foreground">
          This invalidates the current token immediately. The kiosk will disconnect and need to be
          re-paired using the new token.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Rotating…' : 'Rotate token'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Revoke confirmation modal
// ---------------------------------------------------------------------------

interface RevokeConfirmProps {
  kiosk: KioskListItem;
  onCancel: () => void;
  onConfirmed: () => void;
}

function RevokeConfirmModal({ kiosk, onCancel, onConfirmed }: RevokeConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await revokeKiosk(kiosk.id);
      onConfirmed();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md" aria-label="Confirm revoke kiosk">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Revoke "{kiosk.name}"?</h2>
        <p className="text-sm text-muted-foreground">
          This permanently disables the kiosk. The tablet will lose access immediately and cannot be
          re-enabled. You can provision a new kiosk to replace it.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Revoking…' : 'Revoke permanently'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Hard-delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteConfirmProps {
  kiosk: KioskListItem;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DeleteConfirmModal({ kiosk, onCancel, onConfirmed }: DeleteConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await deleteKioskPermanently(kiosk.id);
      onConfirmed();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} aria-label="Confirm delete kiosk" maxWidth="max-w-md">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Delete "{kiosk.name}"?</h2>
        <p className="text-sm text-muted-foreground">
          This will permanently remove the kiosk record. The device is already revoked and cannot
          reconnect. This action cannot be undone.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// One-time token reveal dialog
// ---------------------------------------------------------------------------

interface TokenRevealProps {
  token: string;
  onClose: () => void;
}

function TokenRevealDialog({ token, onClose }: TokenRevealProps) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label="Token issued">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Token issued — copy it now</h2>
        <p className="text-sm text-muted-foreground">
          This token is shown <strong className="text-foreground">once only</strong>. Paste it into
          the kiosk's setup screen now; it cannot be retrieved later.
        </p>
        <div className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
          <code className="num flex-1 break-all text-xs">{token}</code>
          <button
            type="button"
            onClick={() => void copy()}
            className={cn(
              'flex items-center gap-1 rounded-md px-2 py-1 text-xs press',
              copied ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary',
            )}
            aria-label="Copy token"
          >
            {copied ? (
              <Check className="size-3" strokeWidth={3} />
            ) : (
              <Copy className="size-3" strokeWidth={1.75} />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press"
          >
            I've stored it — close
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Kiosk list row
// ---------------------------------------------------------------------------

interface KioskRowProps {
  kiosk: KioskListItem;
  facilityName: string;
  onRotate: () => void;
  onRevoke: () => void;
  onDelete: () => void;
}

function KioskRow({ kiosk, facilityName, onRotate, onRevoke, onDelete }: KioskRowProps) {
  const isRevoked = kiosk.revokedAt !== null;
  return (
    <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] items-center gap-4 px-6 py-3.5 text-sm">
      <span className="flex items-center gap-2 font-medium">
        <Tablet className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        {kiosk.name}
      </span>
      <span className="text-muted-foreground">{facilityName}</span>
      <KioskStatusPill revoked={isRevoked} />
      <span className="num text-xs text-muted-foreground">kk_{kiosk.tokenPrefix}…</span>
      <span className="num text-xs text-muted-foreground">{relativeTime(kiosk.lastSeenAt)}</span>
      <KioskRowActions
        kiosk={kiosk}
        onRotate={onRotate}
        onRevoke={onRevoke}
        onDelete={onDelete}
        isRevoked={isRevoked}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal state union
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'provision' }
  | { kind: 'rotate'; kiosk: KioskListItem }
  | { kind: 'revoke'; kiosk: KioskListItem }
  | { kind: 'delete'; kiosk: KioskListItem }
  | { kind: 'token'; token: string };

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function KiosksAdmin() {
  const { state } = useAuthContext();
  const [kiosks, setKiosks] = useState<KioskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const facilities = state.me?.facilitiesInScope ?? [];

  const refresh = useCallback(async () => {
    try {
      const items = await listKiosks();
      setKiosks(items);
      setPermissionDenied(false);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function facilityName(facilityId: string): string {
    return facilities.find((f) => f.id === facilityId)?.name ?? '—';
  }

  // ------ Render states

  if (loading) {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-10 md:px-10 md:py-14">
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-10 md:px-10 md:py-14">
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Tablet className="mx-auto mb-4 size-10 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-sm font-medium text-foreground">
            You don't have permission to manage kiosks
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Ask an owner or manager to grant access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-10 md:px-10 md:py-14">
      {/* Header */}
      <header className="mb-8 flex items-end justify-between">
        <div>
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Settings
          </p>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Kiosk devices</h1>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'provision' })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Add device
        </button>
      </header>

      {/* Empty state */}
      {kiosks.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Tablet className="mx-auto mb-4 size-10 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-sm font-medium text-foreground">No kiosks paired yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add a kiosk to let check-in tablets connect to this account.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'provision' })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            Add device
          </button>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          {/* Column headers */}
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-4 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            <span>Name</span>
            <span>Facility</span>
            <span>Status</span>
            <span>Token prefix</span>
            <span>Last seen</span>
            <span />
          </div>
          <div className="divide-y divide-border">
            {kiosks.map((kiosk) => (
              <Fragment key={kiosk.id}>
                <KioskRow
                  kiosk={kiosk}
                  facilityName={facilityName(kiosk.facilityId)}
                  onRotate={() => setModal({ kind: 'rotate', kiosk })}
                  onRevoke={() => setModal({ kind: 'revoke', kiosk })}
                  onDelete={() => setModal({ kind: 'delete', kiosk })}
                />
              </Fragment>
            ))}
          </div>
        </section>
      )}

      {/* Modals */}
      {modal.kind === 'provision' && (
        <ProvisionModal
          facilities={facilities}
          onClose={() => setModal({ kind: 'none' })}
          onProvisioned={(_kiosk, token) => {
            setModal({ kind: 'token', token });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'rotate' && (
        <RotateConfirmModal
          kiosk={modal.kiosk}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={(token) => {
            setModal({ kind: 'token', token });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'revoke' && (
        <RevokeConfirmModal
          kiosk={modal.kiosk}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'delete' && (
        <DeleteConfirmModal
          kiosk={modal.kiosk}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'token' && (
        <TokenRevealDialog token={modal.token} onClose={() => setModal({ kind: 'none' })} />
      )}
    </div>
  );
}
